<template>
  <div ref="rootEl">
    <metric-card
      :header="$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADER')"
      :is-live="false"
    >
      <!-- Reload sits next to the "Call History" title via #header-control,
           mirroring the Meetings/Reminders layout. -->
      <template #header-control>
        <woot-button
          v-tooltip.top="$t('OVERVIEW_REPORTS.CALL_HISTORY.RELOAD')"
          icon="arrow-clockwise"
          size="small"
          variant="smooth"
          color-scheme="secondary"
          class="call-history-reload-btn"
          :is-loading="isLoading"
          :aria-label="$t('OVERVIEW_REPORTS.CALL_HISTORY.RELOAD')"
          @click="reloadCallHistory"
        />
      </template>
      <template #control>
        <div class="call-history-controls">
          <div class="multiselect-wrap--small call-history-quick-date z-[3]">
            <multiselect
              :value="selectedQuickDateRange"
              :options="quickDateRangeOptions"
              :searchable="false"
              :show-labels="false"
              :allow-empty="false"
              track-by="id"
              label="name"
              class="no-margin"
              @select="onQuickDateRangeSelect"
            />
          </div>
        <div
          v-on-clickaway="closeFilterPanel"
          class="call-history-filter-anchor"
        >
          <button
            type="button"
            class="call-history-filter-trigger"
            :class="{ 'is-open': isFilterPanelOpen, 'has-active': activeFilterCount > 0 }"
            :aria-label="$t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.TRIGGER_LABEL')"
            :aria-expanded="isFilterPanelOpen ? 'true' : 'false'"
            @click="toggleFilterPanel"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M3 5h18v2.6l-7 7V21l-4-2v-4.4l-7-7V5z"
                fill="currentColor"
              />
            </svg>
            <span
              v-if="activeFilterCount > 0"
              class="call-history-filter-trigger__badge"
            >{{ activeFilterCount }}</span>
          </button>

          <div
            v-if="isFilterPanelOpen"
            class="call-history-filter-panel"
            role="dialog"
            :aria-label="$t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.PANEL_LABEL')"
            @click.stop
          >
            <div class="call-history-filter-panel__header">
              <span class="call-history-filter-panel__title">
                {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.HEADING') }}
              </span>
              <div class="call-history-filter-panel__actions">
                <button
                  type="button"
                  class="call-history-filter-link"
                  :disabled="activeFilterCount === 0 && !hasDraftDifference"
                  @click="clearAllFilters"
                >
                  {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.CLEAR_ALL') }}
                </button>
                <button
                  type="button"
                  class="call-history-filter-apply"
                  @click="applyFilters"
                >
                  {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.APPLY') }}
                </button>
              </div>
            </div>

            <div class="call-history-filter-panel__body">
              <!-- Caller -->
              <div class="call-history-filter-field">
                <span class="call-history-filter-field__label">
                  {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.CALLER') }}
                </span>
                <multiselect
                  v-model="draftFilters.callers"
                  :options="agentOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :show-labels="false"
                  track-by="id"
                  label="name"
                  :placeholder="$t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.CALLER_PLACEHOLDER')"
                  class="call-history-filter-multiselect"
                />
              </div>

              <!-- Status -->
              <div class="call-history-filter-field">
                <span class="call-history-filter-field__label">
                  {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.STATUS') }}
                </span>
                <multiselect
                  v-model="draftFilters.statuses"
                  :options="statusOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="false"
                  :show-labels="false"
                  track-by="value"
                  label="label"
                  :placeholder="$t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.STATUS_PLACEHOLDER')"
                  class="call-history-filter-multiselect"
                  open-direction="bottom"
                />
              </div>

              <!-- Score -->
              <div class="call-history-filter-field">
                <span class="call-history-filter-field__label">
                  {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.SCORE') }}
                </span>
                <div class="call-history-filter-row">
                  <input
                    v-model.number="draftFilters.scoreMin"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    inputmode="numeric"
                    class="call-history-filter-input"
                    :placeholder="$t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.MIN')"
                    @input="clampScoreField('scoreMin')"
                    @change="clampScoreField('scoreMin')"
                    @keydown="blockInvalidScoreKey"
                  />
                  <input
                    v-model.number="draftFilters.scoreMax"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    inputmode="numeric"
                    class="call-history-filter-input"
                    :placeholder="$t('OVERVIEW_REPORTS.CALL_HISTORY.FILTERS.MAX')"
                    @input="clampScoreField('scoreMax')"
                    @change="clampScoreField('scoreMax')"
                    @keydown="blockInvalidScoreKey"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
        </div>
      </template>

      <div class="call-history-container relative w-full">
        <div v-if="showCustomDatePicker" class="custom-date-picker-wrapper">
          <div class="custom-date-picker-row">
            <div class="date-field">
              <label class="date-label">From</label>
              <input
                v-model="customDateFrom"
                type="date"
                class="date-input"
                :max="customDateTo ? (customDateTo < todayYmd ? customDateTo : todayYmd) : todayYmd"
                @change="onFromDateChange"
              />
            </div>
            <div class="date-field">
              <label class="date-label">To</label>
              <input
                v-model="customDateTo"
                type="date"
                class="date-input"
                :min="customDateFrom || undefined"
                :max="todayYmd"
                @change="onToDateChange"
              />
            </div>
            <woot-button
              size="small"
              color-scheme="primary"
              class="apply-btn"
              @click="applyCustomDateRange"
            >
              Apply
            </woot-button>
          </div>
        </div>
        <!-- Fixed-height table area: matches AgentTable's .agent-table-wrap (400px)
             so the two cards stay equal height regardless of row count / state. -->
        <div
          class="call-history-table-wrap relative"
          :style="{ height: tableWrapHeight }"
        >
          <!-- Placeholder shown until IntersectionObserver fires. No request has
               gone out yet — only this lightweight message renders. -->
          <div
            v-if="!hasFetchedOnce && !isLoading"
            class="call-history-state-message"
          >
            {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.WILL_LOAD_ON_SCROLL') }}
          </div>

          <ve-table
            v-else-if="records.length > 0"
            style="height: 100%;"
            :fixed-header="true"
            :columns="columns"
            :table-data="tableData"
            :row-style-option="rowStyleOption"
            :event-custom-option="eventCustomOption"
          />

          <div
            v-else-if="hasFetchedOnce && !records.length && !isLoading"
            class="call-history-empty-overlay"
          >
            <empty-state>
              <h3
                class="text-slate-700 dark:text-slate-200 block text-center w-full text-3xl font-thin"
              >
                {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.EMPTY') }}
              </h3>
            </empty-state>
          </div>
        </div>

        <div
          v-if="isLoading"
          class="call-history-loader-overlay"
        >
          <div class="call-history-loader-content">
            <spinner size="48" />
            <span class="ml-4 font-bold text-base">
              {{ $t('OVERVIEW_REPORTS.CALL_HISTORY.LOADING') }}
            </span>
          </div>
        </div>

        <div v-if="records.length > 0" class="table-pagination">
          <ve-pagination
            :total="meta.total_count"
            :page-index="meta.current_page"
            :page-size="meta.per_page"
            :page-size-option="[meta.per_page]"
            @on-page-number-change="onPageNumberChange"
          />
          <div class="w-full h-[1px]"></div>
        </div>
        <!-- Reserve pagination footer height in every other state so the card never resizes. -->
        <div v-else class="w-full h-[57px]"></div>
      </div>
    </metric-card>
  </div>
</template>

<script lang="jsx">
import { h } from 'vue';
import { mapGetters } from 'vuex';
import { VeTable, VePagination } from 'vue-easytable';
import Multiselect from 'vue-multiselect';
import { mixin as clickaway } from 'vue-clickaway';
import ReportsAPI from 'dashboard/api/reports';
import MetricCard from './MetricCard.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import rtlMixin from 'shared/mixins/rtlMixin';

const DEFAULT_PER_PAGE = 10;
// Fire the first request just before the section scrolls into view.
const OBSERVER_ROOT_MARGIN = '120px';

// Empty filter snapshot. Function so each call returns a fresh, mutable copy.
const emptyFilterDraft = () => ({
  callers: [],
  statuses: [],
  dateFrom: '',
  dateTo: '',
  scoreMin: '',
  scoreMax: '',
});

function formatLocalYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeQuickDateRange(id) {
  const today = new Date();
  let to = today;
  const from = new Date(today);
  switch (id) {
    case 'today':
      break;
    case 'yesterday':
      from.setDate(today.getDate() - 1);
      to = new Date(from);
      break;
    case 'last_7_days':
      from.setDate(today.getDate() - 6);
      break;
    case 'last_30_days':
      from.setDate(today.getDate() - 29);
      break;
    case 'last_3_months':
      from.setMonth(today.getMonth() - 3);
      break;
    case 'last_6_months':
      from.setMonth(today.getMonth() - 6);
      break;
    default:
      break;
  }
  return { dateFrom: formatLocalYmd(from), dateTo: formatLocalYmd(to) };
}

export default {
  name: 'CallHistorySection',
  components: {
    EmptyState,
    MetricCard,
    Multiselect,
    Spinner,
    VeTable,
    VePagination,
  },
  mixins: [rtlMixin, clickaway],
  data() {
    const todayRange = computeQuickDateRange('today');
    return {
      records: [],
      meta: { current_page: 1, per_page: DEFAULT_PER_PAGE, total_count: 0 },
      isLoading: false,
      hasFetchedOnce: false,
      observer: null,
      // Single shared <audio> for the whole table (only one plays at a time).
      audioEl: null,
      // Row id whose recording is currently playing (null when idle).
      activePlayingRowId: null,
      // `appliedFilters` = snapshot used for fetches; `draftFilters` = live form.
      isFilterPanelOpen: false,
      appliedFilters: { ...emptyFilterDraft(), ...todayRange },
      draftFilters: { ...emptyFilterDraft(), ...todayRange },
      showCustomDatePicker: false,
      customDateFrom: '',
      customDateTo: '',
      selectedQuickDateRange: { id: 'today', name: 'Today' },
      // Frozen so ve-table sees a stable reference (avoids row re-renders).
      bodyRowEventsOption: Object.freeze({
        bodyRowEvents: ({ row }) => ({
          click: event => this.onRowClick(event, row),
        }),
      }),
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      agents: 'agents/getAgents',
    }),
    // Agents for the Caller multi-select (with avatars).
    agentOptions() {
      return (this.agents || [])
        .filter(a => a && a.id && a.confirmed !== false)
        .map(a => ({
          id: a.id,
          name: a.name || a.email || `Agent #${a.id}`,
          avatar: a.thumbnail || a.avatar_url || '',
        }));
    },
    statusOptions() {
      return [
        { value: 'SUCCESS', label: 'SUCCESS' },
        { value: 'FAILURE', label: 'FAILURE' },
      ];
    },
    quickDateRangeOptions() {
      return [
        { id: 'today', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.TODAY') },
        { id: 'yesterday', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.YESTERDAY') },
        { id: 'last_7_days', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.LAST_7_DAYS') },
        { id: 'last_30_days', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.LAST_30_DAYS') },
        { id: 'last_3_months', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.LAST_3_MONTHS') },
        { id: 'last_6_months', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.LAST_6_MONTHS') },
        { id: 'custom', name: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.QUICK_DATE_RANGE.CUSTOM') },
      ];
    },
    tableWrapHeight() {
      return this.showCustomDatePicker ? '294px' : '400px';
    },
    todayYmd() {
      return formatLocalYmd(new Date());
    },
    // Active applied-filter count (drives the trigger badge).
    activeFilterCount() {
      return this.countActiveFilters(this.appliedFilters);
    },
    // True when the draft differs from what's applied (order-independent for
    // multiselect arrays; JSON.stringify is unsafe — key order isn't stable).
    hasDraftDifference() {
      return !this.filtersAreEqual(this.draftFilters, this.appliedFilters);
    },
    tableData() {
      return this.records.map(row => {
        const rec = row.call_recording || {};
        const caller = row.caller || {};
        return {
          id: row.id,
          score: rec.agent_communication_score,
          scoreReason: rec.agent_communication_score_reason,
          // Only the URL is fetched up front; audio is loaded lazily on play.
          audioUrl: rec.audio_url || '',
          callerName: caller.name || '',
          callerAvatar: caller.avatar_url || '',
          contact: (row.callee && row.callee.name) || '—',
          createdAt: row.created_at,
          duration: rec.call_duration,
          conversationDisplayId: row.conversation_display_id,
          // Prefers recording.sent_message_id, falls back to initial private note.
          messageId: row.message_id,
          // Show recording status only; no fallback to the dial-time status.
          status: row.call_recording ? rec.status || '' : '',
        };
      });
    },
    columns() {
      const align = this.isRTLView ? 'right' : 'left';
      // Column widths sum to ~625px to fit the 700px .latest-calls-wrapper.
      return [
        {
          field: 'contact',
          key: 'contact',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.CONTACT'),
          align,
          width: 100,
          ellipsis: { showTitle: false, lineClamp: 1 },
          renderBodyCell: ({ row }) => this.renderEllipsisCell(row.contact),
        },
        {
          field: 'caller',
          key: 'caller',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.CALLER'),
          align: 'center',
          width: 70,
          renderBodyCell: ({ row }) => this.renderCallerCell(row),
        },
        {
          field: 'createdAt',
          key: 'createdAt',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.CREATED_AT'),
          align,
          width: 125,
          renderBodyCell: ({ row }) => this.renderCreatedAtCell(row),
        },
        {
          field: 'status',
          key: 'status',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.STATUS'),
          align,
          width: 80,
          renderBodyCell: ({ row }) => this.renderStatusCell(row),
        },
        {
          field: 'duration',
          key: 'duration',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.DURATION'),
          align: 'center',
          width: 85,
          renderBodyCell: ({ row }) => this.formatDuration(row.duration),
        },
        {
          field: 'score',
          key: 'score',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.SCORE'),
          align: 'center',
          width: 100,
          renderBodyCell: ({ row }) => this.renderScoreCell(row),
        },
        {
          field: 'audioUrl',
          key: 'audioUrl',
          title: this.$t('OVERVIEW_REPORTS.CALL_HISTORY.HEADERS.RECORDING'),
          align: 'center',
          width: 100,
          renderBodyCell: ({ row }) => this.renderRecordingCell(row),
        },
      ];
    },
    rowStyleOption() {
      return {
        clickHighlight: false,
        hoverHighlight: true,
      };
    },
    // Row click -> open conversation. Skipped when the click started on an
    // interactive element (audio, link, button). Referentially stable so
    // ve-table doesn't tear down row cells (which would flicker tooltips).
    eventCustomOption() {
      return this.bodyRowEventsOption;
    },
  },
  mounted() {
    this.setupIntersectionObserver();
    // Agents list backs the Caller filter; store dedupes refetches.
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('agents/get');
    }
  },
  beforeDestroy() {
    this.teardownObserver();
    this.teardownAudio();
  },
  methods: {
    setupIntersectionObserver() {
      if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
        this.fetchPage(1);
        return;
      }
      this.observer = new IntersectionObserver(
        entries => {
          const visible = entries.some(entry => entry.isIntersecting);
          if (visible && !this.hasFetchedOnce && !this.isLoading) {
            this.fetchPage(1);
            this.teardownObserver();
          }
        },
        { rootMargin: OBSERVER_ROOT_MARGIN, threshold: 0.01 }
      );
      this.observer.observe(this.$refs.rootEl);
    },
    teardownObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    async fetchPage(page) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const { data } = await ReportsAPI.getCallHistory({
          page,
          perPage: this.meta.per_page,
          filters: this.serializeFilters(this.appliedFilters),
        });
        this.records = data?.data || [];
        this.meta = {
          current_page: data?.meta?.current_page || page,
          per_page: data?.meta?.per_page || this.meta.per_page,
          total_count: data?.meta?.total_count || 0,
        };
        this.hasFetchedOnce = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('CallHistorySection: failed to fetch call history', e);
        this.records = [];
        this.hasFetchedOnce = true;
      } finally {
        this.isLoading = false;
      }
    },
    onPageNumberChange(pageIndex) {
      this.fetchPage(pageIndex);
    },
    // Refetches the current page so the user stays where they were.
    reloadCallHistory() {
      this.fetchPage(this.meta.current_page || 1);
    },
    toggleFilterPanel() {
      if (this.isFilterPanelOpen) {
        this.closeFilterPanel();
        return;
      }
      // Resume the draft from the applied snapshot.
      this.draftFilters = this.cloneFilters(this.appliedFilters);
      this.isFilterPanelOpen = true;
    },
    closeFilterPanel() {
      if (!this.isFilterPanelOpen) return;
      this.isFilterPanelOpen = false;
    },
    clampScoreField(field) {
      const raw = this.draftFilters[field];
      if (raw === '' || raw === null || raw === undefined) return;
      const n = Math.trunc(Number(raw));
      if (!Number.isFinite(n)) {
        this.draftFilters[field] = '';
        return;
      }
      let clamped = n;
      if (clamped < 0) clamped = 0;
      if (clamped > 100) clamped = 100;
      if (clamped !== raw) {
        this.draftFilters[field] = clamped;
      }
    },
    blockInvalidScoreKey(event) {
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End',
      ];
      if (allowedKeys.includes(event.key)) return;
      if (event.ctrlKey || event.metaKey) return;
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    },
    applyFilters() {
      this.appliedFilters = this.cloneFilters(this.draftFilters);
      this.closeFilterPanel();
      // Filter change resets to page 1.
      this.fetchPage(1);
    },
    clearAllFilters() {
      const { dateFrom, dateTo } = this.draftFilters;
      this.draftFilters = { ...emptyFilterDraft(), dateFrom, dateTo };
      if (this.activeFilterCount > 0) {
        this.appliedFilters = {
          ...emptyFilterDraft(),
          dateFrom: this.appliedFilters.dateFrom,
          dateTo: this.appliedFilters.dateTo,
        };
        this.fetchPage(1);
      }
    },
    onQuickDateRangeSelect(option) {
      if (!option || !option.id) return;
      this.selectedQuickDateRange = option;
      if (option.id === 'custom') {
        const todayYmd = formatLocalYmd(new Date());
        this.customDateFrom = this.appliedFilters.dateFrom || todayYmd;
        this.customDateTo = this.appliedFilters.dateTo || todayYmd;
        this.showCustomDatePicker = true;
        return;
      }
      this.showCustomDatePicker = false;
      const range = computeQuickDateRange(option.id);
      this.appliedFilters = { ...this.appliedFilters, ...range };
      this.draftFilters = { ...this.draftFilters, ...range };
      if (this.hasFetchedOnce) {
        this.fetchPage(1);
      }
    },
    onFromDateChange() {
      if (this.customDateFrom && this.customDateFrom > this.todayYmd) {
        this.customDateFrom = this.todayYmd;
      }
      if (this.customDateTo && this.customDateFrom > this.customDateTo) {
        this.customDateTo = this.customDateFrom;
      }
    },
    onToDateChange() {
      if (this.customDateTo && this.customDateTo > this.todayYmd) {
        this.customDateTo = this.todayYmd;
      }
      if (this.customDateFrom && this.customDateTo < this.customDateFrom) {
        this.customDateTo = this.customDateFrom;
      }
    },
    applyCustomDateRange() {
      if (!this.customDateFrom || !this.customDateTo) return;
      if (this.customDateTo < this.customDateFrom) return;
      if (this.customDateFrom > this.todayYmd || this.customDateTo > this.todayYmd) return;
      const range = { dateFrom: this.customDateFrom, dateTo: this.customDateTo };
      this.appliedFilters = { ...this.appliedFilters, ...range };
      this.draftFilters = { ...this.draftFilters, ...range };
      if (this.hasFetchedOnce) {
        this.fetchPage(1);
      }
    },
    cloneFilters(src) {
      return {
        callers: Array.isArray(src.callers) ? src.callers.slice() : [],
        statuses: Array.isArray(src.statuses) ? src.statuses.slice() : [],
        dateFrom: src.dateFrom || '',
        dateTo: src.dateTo || '',
        scoreMin: src.scoreMin === '' || src.scoreMin == null ? '' : src.scoreMin,
        scoreMax: src.scoreMax === '' || src.scoreMax == null ? '' : src.scoreMax,
      };
    },
    // Maps the local filter object into the camelCase shape expected by ReportsAPI.
    serializeFilters(src) {
      return {
        callerIds: (src.callers || []).map(c => c.id).filter(Boolean),
        statuses: (src.statuses || []).map(s => s.value).filter(Boolean),
        dateFrom: src.dateFrom || '',
        dateTo: src.dateTo || '',
        scoreMin: this.toFilterNumber(src.scoreMin),
        scoreMax: this.toFilterNumber(src.scoreMax),
      };
    },
    toFilterNumber(value) {
      if (value === '' || value == null) return undefined;
      const n = Number(value);
      if (!Number.isFinite(n) || n < 0) return undefined;
      return n;
    },
    countActiveFilters(src) {
      let n = 0;
      if ((src.callers || []).length > 0) n += 1;
      if ((src.statuses || []).length > 0) n += 1;
      if (this.toFilterNumber(src.scoreMin) != null || this.toFilterNumber(src.scoreMax) != null) n += 1;
      return n;
    },
    // Field-by-field equality (arrays compared order-independently).
    filtersAreEqual(a, b) {
      if (a === b) return true;
      if (!a || !b) return false;

      const callersA = (a.callers || []).map(c => (c && c.id != null ? c.id : c));
      const callersB = (b.callers || []).map(c => (c && c.id != null ? c.id : c));
      if (!this.idListsEqual(callersA, callersB)) return false;

      const statusesA = (a.statuses || []).map(s => (s && s.value != null ? s.value : s));
      const statusesB = (b.statuses || []).map(s => (s && s.value != null ? s.value : s));
      if (!this.idListsEqual(statusesA, statusesB)) return false;

      if ((a.dateFrom || '') !== (b.dateFrom || '')) return false;
      if ((a.dateTo || '') !== (b.dateTo || '')) return false;

      // Normalise so '' / null / 'abc' collapse to a single empty sentinel.
      if (this.toFilterNumber(a.scoreMin) !== this.toFilterNumber(b.scoreMin)) return false;
      if (this.toFilterNumber(a.scoreMax) !== this.toFilterNumber(b.scoreMax)) return false;

      return true;
    },
    idListsEqual(a, b) {
      if (a.length !== b.length) return false;
      const sortedA = a.map(String).sort();
      const sortedB = b.map(String).sort();
      for (let i = 0; i < sortedA.length; i += 1) {
        if (sortedA[i] !== sortedB[i]) return false;
      }
      return true;
    },
    onRowClick(event, row) {
      // Don't navigate when the click started on an inner interactive element.
      if (
        event &&
        event.target &&
        event.target.closest('a, button, input, .call-history-play-btn')
      ) {
        return;
      }
      if (!row.conversationDisplayId || !this.currentAccountId) return;
      // ?messageId=… is the ConversationView contract that scrolls to the message.
      const conversationPath = `/app/accounts/${this.currentAccountId}/conversations/${row.conversationDisplayId}`;
      const target = row.messageId
        ? { path: conversationPath, query: { messageId: String(row.messageId) } }
        : conversationPath;
      this.$router.push(target);
    },
    formatDateTime(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      // Compact "5/27/26, 9:44 PM" — shorter than toLocaleString's default.
      try {
        return new Intl.DateTimeFormat(undefined, {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(d);
      } catch (_) {
        return d.toLocaleString();
      }
    },
    formatDuration(seconds) {
      const s = Number(seconds);
      if (!Number.isFinite(s) || s <= 0) return '—';
      const hours = Math.floor(s / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      const secs = s % 60;
      if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
      if (minutes > 0) return `${minutes}m ${secs}s`;
      return `${secs}s`;
    },
    // Status text is rendered raw; chip color is derived from the value.
    statusClass(status) {
      const label = (status || '').toString().toUpperCase();
      const base = 'inline-block px-2 py-0.5 text-xs rounded whitespace-nowrap';
      if (label === 'SUCCESS') {
        return `${base} bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300`;
      }
      if (label === 'FAILURE') {
        return `${base} bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300`;
      }
      return `${base} bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200`;
    },
    // Stable v-tooltip options (no flip, body-anchored) — prevents flicker
    // when ve-table re-renders rows on hover.
    tooltipOptions(text) {
      return {
        content: text,
        placement: 'top',
        container: 'body',
        delay: { show: 120, hide: 0 },
        popperOptions: {
          modifiers: {
            flip: { enabled: false },
            preventOverflow: { boundariesElement: 'viewport' },
          },
        },
      };
    },
    renderEllipsisCell(value) {
      const text = value == null || value === '' ? '—' : String(value);
      return h('div', { class: 'call-history-ellipsis-cell' }, [
        h(
          'span',
          {
            class: 'call-history-ellipsis-cell__text',
            directives: [
              { name: 'tooltip', value: this.tooltipOptions(text) },
            ],
          },
          text
        ),
      ]);
    },
    renderScoreCell(row) {
      const numericScore = Number(row.score);
      if (
        row.score == null ||
        row.score === '' ||
        !Number.isFinite(numericScore) ||
        numericScore < 0
      ) {
        return h('span', { style: { color: '#94a3b8' } }, '—');
      }
      // "/100" denominator lives in the column header to keep cells compact.
      const label = `${row.score}`;
      const data = {
        class: 'text-slate-800 dark:text-slate-100',
        style: { whiteSpace: 'nowrap' },
      };
      if (row.scoreReason) {
        data.directives = [
          {
            name: 'tooltip',
            value: this.tooltipOptions(String(row.scoreReason)),
          },
        ];
        data.style.cursor = 'help';
      }
      return h('span', data, label);
    },
    // Caller cell: 28px circular avatar with a name tooltip on hover.
    renderCallerCell(row) {
      const name = row.callerName || '';
      const tooltipContent = name || this.$t('OVERVIEW_REPORTS.CALL_HISTORY.UNKNOWN_CALLER');
      const tooltipDirective = {
        name: 'tooltip',
        value: this.tooltipOptions(tooltipContent),
      };
      if (row.callerAvatar) {
        return h(
          'div',
          { class: 'call-history-caller-cell' },
          [
            h('img', {
              class: 'call-history-caller-avatar',
              attrs: { src: row.callerAvatar, alt: name },
              directives: [tooltipDirective],
            }),
          ]
        );
      }
      return h('div', { class: 'call-history-caller-cell' }, [
        h(
          'div',
          {
            class:
              'call-history-caller-avatar call-history-caller-avatar--placeholder',
            directives: [tooltipDirective],
          },
          [
            h(
              'svg',
              {
                attrs: {
                  xmlns: 'http://www.w3.org/2000/svg',
                  viewBox: '0 0 24 24',
                  width: '14',
                  height: '14',
                  'aria-hidden': 'true',
                  focusable: 'false',
                },
              },
              [
                h('path', {
                  attrs: {
                    d:
                      'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
                    fill: 'currentColor',
                  },
                }),
              ]
            ),
          ]
        ),
      ]);
    },
    // Lazily build the shared Audio element used by every row.
    ensureAudioEl() {
      if (this.audioEl) return this.audioEl;
      const el = new Audio();
      el.preload = 'none';
      // Keep icon state in sync with the element (end-of-track, OS pause, …).
      const clearActive = () => {
        this.activePlayingRowId = null;
      };
      el.addEventListener('ended', clearActive);
      el.addEventListener('pause', clearActive);
      el.addEventListener('error', clearActive);
      this.audioEl = el;
      return el;
    },
    teardownAudio() {
      if (!this.audioEl) return;
      try {
        this.audioEl.pause();
      } catch (_) {
        // pause can throw on unloaded media in some browsers.
      }
      this.audioEl.src = '';
      this.audioEl = null;
      this.activePlayingRowId = null;
    },
    toggleRecording(row) {
      if (!row || !row.audioUrl) return;
      const audio = this.ensureAudioEl();
      if (this.activePlayingRowId === row.id) {
        audio.pause();
        return;
      }
      // Switching rows: pause current and load the new src from the start.
      audio.pause();
      if (audio.src !== row.audioUrl) {
        audio.src = row.audioUrl;
      } else {
        audio.currentTime = 0;
      }
      const playPromise = audio.play();
      this.activePlayingRowId = row.id;
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay block / network error.
          if (this.activePlayingRowId === row.id) {
            this.activePlayingRowId = null;
          }
        });
      }
    },
    // Inline play/pause SVGs (fluent-icon set has no equivalents).
    renderPlayPauseIcon(isPlaying) {
      const path = isPlaying
        ? // Two vertical bars.
          'M6 5h4v14H6zM14 5h4v14h-4z'
        : // Right-pointing triangle.
          'M8 5v14l11-7z';
      return h(
        'svg',
        {
          attrs: {
            xmlns: 'http://www.w3.org/2000/svg',
            viewBox: '0 0 24 24',
            width: '14',
            height: '14',
            'aria-hidden': 'true',
            focusable: 'false',
          },
        },
        [
          h('path', {
            attrs: { d: path, fill: 'currentColor' },
          }),
        ]
      );
    },
    renderRecordingCell(row) {
      if (!row.audioUrl) {
        return h('span', { style: { color: '#94a3b8' } }, '—');
      }
      const isPlaying = this.activePlayingRowId === row.id;
      return h(
        'button',
        {
          class: [
            'call-history-play-btn',
            isPlaying ? 'is-playing' : '',
          ],
          attrs: {
            type: 'button',
            'aria-label': isPlaying
              ? this.$t('OVERVIEW_REPORTS.CALL_HISTORY.PAUSE_RECORDING')
              : this.$t('OVERVIEW_REPORTS.CALL_HISTORY.PLAY_RECORDING'),
          },
          on: {
            click: event => {
              // Don't let row click navigate when toggling playback.
              event.stopPropagation();
              this.toggleRecording(row);
            },
          },
          directives: [
            {
              name: 'tooltip',
              value: this.tooltipOptions(
                isPlaying
                  ? this.$t('OVERVIEW_REPORTS.CALL_HISTORY.PAUSE_RECORDING')
                  : this.$t('OVERVIEW_REPORTS.CALL_HISTORY.PLAY_RECORDING')
              ),
            },
          ],
        },
        [this.renderPlayPauseIcon(isPlaying)]
      );
    },
    renderCreatedAtCell(row) {
      const text = this.formatDateTime(row.createdAt);
      return h('span', { style: { color: '#64748b', fontSize: '12px' } }, text);
    },
    renderStatusCell(row) {
      const raw = (row.status || '').toString();
      if (!raw) return h('span', { style: { color: '#94a3b8' } }, '—');
      return h('span', { class: this.statusClass(raw) }, raw);
    },
  },
};
</script>

<style lang="scss" scoped>
.call-history-container {
  @apply flex flex-col flex-1;
  position: relative;

  // Fixed-height area; mirrors AgentTable's .agent-table-wrap.
  .call-history-table-wrap {
    height: 400px;
  }

  // Hide scrollbar tracks; vertical scroll still works.
  .call-history-table-wrap :deep(.ve-table-container) {
    overflow-x: hidden !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .call-history-table-wrap :deep(.ve-table-container::-webkit-scrollbar) {
    width: 0;
    height: 0;
    display: none;
  }

  .call-history-state-message {
    @apply text-slate-500 dark:text-slate-300 text-sm text-center;
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .call-history-empty-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ve-table {
    &:deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        // Tighter horizontal padding (8px) so narrow headers don't clip.
        padding: var(--space-small) var(--space-small) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-small) !important;
      }
    }
  }

  // Compact play/pause button used in the Recording column.
  :deep(.call-history-play-btn) {
    @apply inline-flex items-center justify-center;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 9999px;
    border: 1px solid var(--s-200, #e2e8f0);
    background-color: var(--s-50, #f8fafc);
    color: var(--s-700, #334155);
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease,
      border-color 0.15s ease;
  }

  :deep(.call-history-play-btn:hover) {
    background-color: var(--w-50, #eef2ff);
    border-color: var(--w-200, #c7d2fe);
    color: var(--w-700, #4338ca);
  }

  :deep(.call-history-play-btn.is-playing) {
    background-color: var(--w-500, #6366f1);
    border-color: var(--w-500, #6366f1);
    color: #ffffff;
  }

  :deep(.call-history-play-btn:focus) {
    outline: none;
    box-shadow: 0 0 0 2px var(--w-200, #c7d2fe);
  }

  // Make the inline SVG follow the button's text color.
  :deep(.call-history-play-btn svg) {
    display: block;
    fill: currentColor;
  }

  :deep(.call-history-caller-cell) {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
  }

  :deep(.call-history-caller-avatar) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #40518a;
    display: block;
    cursor: default;
  }

  :deep(.call-history-caller-avatar--placeholder) {
    background-color: #e8ecf4;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #40518a;
  }

  // Single-line ellipsised cell. The inner inline-block hosts the tooltip so
  // it stays centered over the visible text (not the full cell width).
  :deep(.call-history-ellipsis-cell) {
    overflow: hidden;
    max-width: 100%;
  }

  :deep(.call-history-ellipsis-cell__text) {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }

  :deep(.ve-table-body-tr) {
    cursor: pointer;
  }
  :deep(.ve-table-body-tr:hover) {
    background-color: rgba(15, 23, 42, 0.04);
  }

  &:deep(.ve-pagination) {
    @apply bg-transparent dark:bg-transparent;
  }

  &:deep(.ve-pagination-select) {
    @apply hidden;
  }

  .table-pagination {
    @apply mt-4 text-right;
  }
}

.call-history-loader-overlay {
  @apply bg-white/90 dark:bg-slate-900/90;
  position: absolute;
  top: 0;
  left: 0;
  // Above ve-table's fixed header (z-index ~5).
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-history-loader-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* -- Filter trigger + panel --------------------------------------------- */

// Anchor for the absolutely-positioned filter panel.
/* Sized to match the filter trigger next to it. */
.call-history-reload-btn {
  min-width: 28px;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.call-history-controls {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.call-history-quick-date {
  width: 160px;
  min-width: 140px;
}

.custom-date-picker-wrapper {
  padding-bottom: var(--space-small);
  min-height: 100px;
  width: 100%;
  display: block;
}

.custom-date-picker-row {
  @apply bg-slate-50 dark:bg-slate-800 border border-solid border-slate-100 dark:border-slate-600;
  display: inline-flex;
  align-items: flex-end;
  gap: var(--space-normal);
  padding: var(--space-small) var(--space-normal);
  border-radius: var(--border-radius-normal);
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-label {
  @apply text-slate-600 dark:text-slate-300;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.date-input {
  @apply bg-white dark:bg-slate-900 border border-solid border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-100;
  padding: 8px 12px;
  border-radius: var(--border-radius-normal);
  font-size: 13px;
  min-width: 130px;
  height: 36px;
}

.date-input:focus {
  outline: none;
  border-color: var(--w-500);
  box-shadow: 0 0 0 2px var(--w-75);
}

.apply-btn {
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--border-radius-normal);
  white-space: nowrap;
  align-self: center;
  margin-top: 0.75rem;
}

.call-history-filter-anchor {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.call-history-filter-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 6px;
  border: 1px solid var(--s-200, #e2e8f0);
  background-color: var(--s-50, #f8fafc);
  color: var(--s-700, #334155);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;

  &:hover,
  &:focus {
    background-color: var(--w-50, #eef2ff);
    border-color: var(--w-200, #c7d2fe);
    color: var(--w-700, #4338ca);
    outline: none;
  }

  &.is-open,
  &.has-active {
    background-color: var(--w-500, #6366f1);
    border-color: var(--w-500, #6366f1);
    color: #ffffff;
  }
}

.call-history-filter-trigger__badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--r-500, #ef4444);
  color: #ffffff;
  font-size: 10px;
  line-height: 16px;
  font-weight: 600;
  text-align: center;
  pointer-events: none;
}

.call-history-filter-panel {
  @apply bg-white dark:bg-slate-800 border border-solid border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-50;
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 22;
  width: 320px;
  max-width: 90vw;
  // Cap height so the panel can never extend off-screen.
  max-height: min(520px, 80vh);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-size: 13px;
}

.call-history-filter-panel__header {
  @apply border-b border-solid border-slate-100 dark:border-slate-700;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  // Pin header so Apply / Clear all stay visible.
  position: sticky;
  top: 0;
  background: inherit;
  z-index: 1;
  flex-shrink: 0;
}

.call-history-filter-panel__title {
  @apply text-slate-600 dark:text-slate-200;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  flex: 1;
  min-width: 0;
}

.call-history-filter-panel__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.call-history-filter-panel__body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  // Scrollable body inside the capped panel.
  flex: 1 1 auto;
  min-height: 0;
}

.call-history-filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.call-history-filter-field__label {
  @apply text-slate-500 dark:text-slate-300;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.call-history-filter-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.call-history-filter-input {
  @apply bg-white dark:bg-slate-900 border border-solid border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50;
  width: 100%;
  padding: 6px 8px;
  font-size: 13px;
  line-height: 1.4;
  border-radius: 4px;
  height: 32px;

  &:focus {
    outline: none;
    border-color: var(--w-400, #818cf8);
    box-shadow: 0 0 0 2px var(--w-100, #e0e7ff);
  }
}

// Tighter multiselect sizing so the panel stays within 320px.
.call-history-filter-multiselect {
  &:deep(.multiselect__tags) {
    min-height: 32px;
    padding: 4px 28px 0 12px;
    font-size: 13px;
    border-color: var(--s-200, #e2e8f0);
  }

  &:deep(.multiselect__placeholder) {
    padding-left: 4px;
  }

  &:deep(.multiselect__select) {
    height: 30px;
  }

  &:deep(.multiselect__tag) {
    background: var(--w-50, #eef2ff);
    color: var(--w-700, #4338ca);
    margin-right: 4px;
  }

  &:deep(.multiselect__option--highlight) {
    background: var(--w-500, #6366f1);
  }
}

.call-history-filter-link {
  background: none;
  border: none;
  padding: 4px 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--w-600, #4f46e5);
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
    outline: none;
  }

  &:disabled {
    color: var(--s-400, #94a3b8);
    cursor: not-allowed;
    text-decoration: none;
  }
}

.call-history-filter-apply {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  background: var(--w-500, #6366f1);
  border: 1px solid var(--w-500, #6366f1);
  border-radius: 4px;
  cursor: pointer;
  line-height: 1.2;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: var(--w-600, #4f46e5);
    border-color: var(--w-600, #4f46e5);
    outline: none;
  }
}
</style>
